import { h } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { useJsonProvider } from '../JsonProvider';
import { OrthomolIcon } from '../../components-common/OrthomolIcon';
import type { RecommendedProduct } from '../types';
import { useShopware } from '../shopware/ShopwareContext';
import { getFormattedPrices, checkProductAvailability } from '../shopware/shopwareUtils';
import type { Product as ShopwareProduct } from '../shopware/shopwareApi';
import { sanitizeHtml } from '../utils/sanitizeHtml';
import { URLS } from '../Urls';
import { buildGA4Item, pushAddToCart } from '../tracking/ecommerceDataLayer';

export const CartRecommendations = () => {
  const { recommendedProducts } = useJsonProvider();
  const { addProduct, fetchProductsByNumbers } = useShopware();
  const [currentPage, setCurrentPage] = useState(1);
  const [isPrevDisabled, setIsPrevDisabled] = useState(true);
  const [isNextDisabled, setIsNextDisabled] = useState(false);
  const [isLoadingProducts, setIsLoadingProducts] = useState(true);
  const [shopwareProductsMap, setShopwareProductsMap] = useState<Map<string, ShopwareProduct>>(new Map());
  const [addingToCart, setAddingToCart] = useState(false);
  const listRef = useRef<HTMLUListElement>(null);

  // Fetch shopware products for all recommendations
  useEffect(() => {
    const loadProducts = async () => {
      if (recommendedProducts.error || !recommendedProducts.data || recommendedProducts.data.length === 0) {
        setIsLoadingProducts(false);
        return;
      }

      const products = recommendedProducts.data as RecommendedProduct[];

      // Collect all product numbers
      const allONumbers: string[] = [];
      products.forEach((product) => {
        const oNumber = product.dosageForms?.[0]?.packsizes?.[0]?.oNumber;
        if (oNumber) {
          allONumbers.push(oNumber);
        }
      });

      const productNumbers = Array.from(new Set(allONumbers));

      // Fetch all products
      if (productNumbers.length > 0) {
        try {
          const productsMap = await fetchProductsByNumbers(productNumbers);
          setShopwareProductsMap(productsMap);
        } catch (error) {
          console.error('Failed to load products:', error);
        }
      }

      setIsLoadingProducts(false);
    };

    loadProducts();
  }, [recommendedProducts, fetchProductsByNumbers]);

  // Handle error or loading state
  if (recommendedProducts.error || !recommendedProducts.data || recommendedProducts.data.length === 0) {
    return null;
  }

  // Show loading spinner while loading products
  if (isLoadingProducts) {
    return (
      <div id="shopping-cart-recommendations" className="pb-4 pt-md-5 pt-lg-4 d-none d-md-block">
        <h6 className="text-primary lh-1">Unsere Empfehlungen für Dich:</h6>
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Lade Empfehlungen...</span>
          </div>
        </div>
      </div>
    );
  }

  const products = recommendedProducts.data as RecommendedProduct[];
  const pagesTotal = Math.ceil(products.length / 2);

  // Handle add to cart
  const handleAddToCart = async (oNumber: string, product: RecommendedProduct) => {
    if (addingToCart) return;

    const shopwareProduct = shopwareProductsMap.get(oNumber);
    if (!shopwareProduct) return;

    const isAvailable = checkProductAvailability(shopwareProduct);
    if (!isAvailable) return;

    setAddingToCart(true);

    try {
      // Pass this product's o-number so a ?pron=-scoped code only attaches for products in the
      // newsletter's list (ORT-3077).
      await addProduct(shopwareProduct.id, 1, oNumber);

      const dosageForm = product.dosageForms?.[0];
      const packsize = dosageForm?.packsizes?.[0];
      if (dosageForm && packsize) {
        const ga4Item = buildGA4Item(packsize, dosageForm, shopwareProduct, 1);
        if (ga4Item) {
          pushAddToCart(ga4Item);
        }
      }
    } catch (error) {
      console.error('Failed to add product to cart:', error);
    } finally {
      setAddingToCart(false);
    }
  };

  // Extract color from bgClass (e.g., 'text-arthro' -> 'arthro')
  const extractColor = (bgClass: string | undefined) => {
    if (!bgClass) return 'primary';
    return bgClass.replace(/^text-/, '');
  };

  const updateButtonStates = () => {
    if (!listRef.current) return;

    const list = listRef.current;
    const itemWidth = list.querySelector('li')?.offsetWidth || 0;
    const listGap = parseInt(window.getComputedStyle(list).getPropertyValue('column-gap')) || 0;

    setIsPrevDisabled(list.scrollLeft === 0);
    setIsNextDisabled(list.scrollLeft >= list.scrollWidth - (itemWidth + listGap) * 2);
  };

  const handleScroll = () => {
    if (!listRef.current) return;

    const list = listRef.current;
    const itemWidth = list.querySelector('li')?.offsetWidth || 0;
    const newPage = Math.round(list.scrollLeft / (itemWidth * 2)) + 1;
    setCurrentPage(newPage);
    updateButtonStates();
  };

  const handlePaginationClick = (direction: 'prev' | 'next') => {
    if (!listRef.current) return;

    const newPage = direction === 'prev' ? Math.max(currentPage - 1, 1) : Math.min(currentPage + 1, pagesTotal);

    setCurrentPage(newPage);

    // Scroll to the appropriate item
    const targetIndex = 2 * newPage - 1;
    const targetItem = listRef.current.querySelector(`li:nth-child(${targetIndex})`);

    if (targetItem) {
      targetItem.scrollIntoView({
        inline: products.length % 2 === 0 ? 'start' : direction === 'prev' ? 'end' : 'start',
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    updateButtonStates();
  }, [products.length]);

  return (
    <div id="shopping-cart-recommendations" className="pb-4 pt-md-5 pt-lg-4 d-none d-md-block">
      <h6 className="text-primary lh-1">Unsere Empfehlungen für Dich:</h6>
      <ul
        ref={listRef}
        id="shopping-cart-recommendations-list"
        className="shopping-cart-recommendations-list gap-2"
        onScroll={handleScroll}
      >
        {products.map((product) => {
          const dosageForm = product.dosageForms?.[0];
          const packsize = dosageForm?.packsizes?.[0];
          const oNumber = packsize?.oNumber || '';
          const shopwareProduct = shopwareProductsMap.get(oNumber);

          // Get pricing and availability from shopware product
          const isAvailable = shopwareProduct ? checkProductAvailability(shopwareProduct) : false;
          const { unitPrice: priceItem, referencePrice: priceUnit } = shopwareProduct
            ? getFormattedPrices(shopwareProduct)
            : { unitPrice: '-', referencePrice: null };

          return (
            <li
              key={product.id}
              className={`bg-gray-200 d-flex flex-column align-items-center gap-4 p-2 border-bottom border-8 border-${extractColor(product.bgClass)}`}
            >
              {product.packshotHtml && (
                <div className="shopping-cart-item-visual" aria-hidden="true">
                  <a
                    href={oNumber ? URLS.productRedirectWithONumber(oNumber) : '#'}
                    tabIndex={-1}
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(product.packshotHtml) }}
                  />
                </div>
              )}
              <div className="d-flex flex-column gap-2 flex-grow-1 text-center">
                <h6 className="text-primary mb-0">
                  <a href={oNumber ? URLS.productRedirectWithONumber(oNumber) : '#'} className="text-decoration-none">
                    {product.name}
                  </a>
                </h6>
                <small className="mb-auto">
                  {dosageForm?.name} {packsize?.size}
                </small>
                <p className="product-variant-priceinfo flex-row justify-content-center align-items-center gap-2 mb-1">
                  <strong className="lh-1 text-nowrap">
                    <span>{priceItem}</span>
                  </strong>
                  {priceUnit && <small className="text-nowrap">{priceUnit}</small>}
                </p>
                <button
                  className="small btn btn-secondary justify-content-center mt-3 p-2 w-100"
                  disabled={!isAvailable || addingToCart}
                  onClick={() => handleAddToCart(oNumber, product)}
                >
                  <span>{isAvailable ? 'Hinzufügen' : 'Nicht verfügbar'}</span>
                </button>
              </div>
              {product.healthClaims && (
                <div
                  className="product-health-claim text-center"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(product.healthClaims) }}
                />
              )}
            </li>
          );
        })}
      </ul>
      <div className="shopping-cart-recommendations-list-pagination d-flex justify-content-end gap-2">
        <button
          className="btn rounded-circle p-0 border-0 justify-content-center bg-gray-200 text-primary"
          onClick={() => handlePaginationClick('prev')}
          aria-label="Zurück blättern"
          aria-controls="shopping-cart-recommendations-list"
          disabled={isPrevDisabled}
        >
          <OrthomolIcon name="arrow-24" size="24" rotation={180} />
        </button>
        <button
          className="btn rounded-circle p-0 border-0 justify-content-center bg-gray-200 text-primary"
          onClick={() => handlePaginationClick('next')}
          aria-label="Vorwärts blättern"
          aria-controls="shopping-cart-recommendations-list"
          disabled={isNextDisabled}
        >
          <OrthomolIcon name="arrow-24" size="24" />
        </button>
      </div>
    </div>
  );
};
